import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from './client';
import { useAuth } from './AuthContext';
import type { Item, ItemType, Tag } from './types';

interface VaultContextValue {
  items: Item[];
  tags: Tag[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  createItem: (
    item: {
      title: string;
      item_type: ItemType;
      payload: Record<string, any>;
      favorite?: boolean;
      storage_ref?: string | null;
      storage_meta?: any;
    },
    tagNames?: string[]
  ) => Promise<{ data: Item | null; error: string | null }>;
  updateItem: (
    id: string,
    updates: {
      title?: string;
      payload?: Record<string, any>;
      favorite?: boolean;
      storage_ref?: string | null;
      storage_meta?: any;
    },
    tagNames?: string[]
  ) => Promise<{ data: Item | null; error: string | null }>;
  deleteItem: (id: string) => Promise<{ error: string | null }>;
  toggleFavorite: (id: string) => Promise<void>;
  createTag: (name: string) => Promise<{ data: Tag | null; error: string | null }>;
  deleteTag: (id: string) => Promise<{ error: string | null }>;
}

const VaultContext = createContext<VaultContextValue | null>(null);

export function VaultProvider({ children }: { children: React.ReactNode }) {
  const { session } = useAuth();
  const [items, setItems] = useState<Item[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all items and tags
  const fetchData = useCallback(async () => {
    if (!session?.user?.id) return;
    setLoading(true);
    setError(null);

    try {
      // 1. Fetch tags
      const { data: tagsData, error: tagsError } = await supabase
        .from('tags')
        .select('*')
        .order('name');

      if (tagsError) throw tagsError;

      // 2. Fetch items with their tags
      const { data: itemsData, error: itemsError } = await supabase
        .from('items')
        .select(`
          *,
          item_tags (
            tag_id,
            tags (
              id,
              name,
              created_at
            )
          )
        `)
        .order('updated_at', { ascending: false });

      if (itemsError) throw itemsError;

      // Map the nested join query to a clean flattened tags array
      const mappedItems: Item[] = (itemsData || []).map((item: any) => {
        const itemTags = item.item_tags || [];
        const flatTags = itemTags
          .map((it: any) => it.tags)
          .filter(Boolean) as Tag[];

        return {
          id: item.id,
          user_id: item.user_id,
          item_type: item.item_type,
          title: item.title,
          payload: item.payload,
          storage_ref: item.storage_ref,
          storage_meta: item.storage_meta,
          favorite: item.favorite,
          created_at: item.created_at,
          updated_at: item.updated_at,
          tags: flatTags,
        };
      });

      setTags(tagsData || []);
      setItems(mappedItems);
    } catch (err: any) {
      console.error('[vault] Error fetching vault data:', err);
      setError(err.message || 'Failed to load vault contents.');
    } finally {
      setLoading(false);
    }
  }, [session]);

  // Fetch data when session changes
  useEffect(() => {
    if (session) {
      fetchData();
    } else {
      setItems([]);
      setTags([]);
    }
  }, [session, fetchData]);

  // Tag helper: ensures tags exist and returns their IDs
  const ensureTags = useCallback(
    async (tagNames: string[]): Promise<{ ids: string[]; error: string | null }> => {
      if (!session?.user?.id || tagNames.length === 0) return { ids: [], error: null };

      try {
        const resolvedIds: string[] = [];
        const cleanNames = tagNames.map((t) => t.trim()).filter((t) => t.length > 0);

        // Find existing tags in our state to avoid redundant DB insertions
        const existingMap = new Map(tags.map((t) => [t.name.toLowerCase(), t]));
        const toCreate: string[] = [];

        for (const name of cleanNames) {
          const matched = existingMap.get(name.toLowerCase());
          if (matched) {
            resolvedIds.push(matched.id);
          } else {
            toCreate.push(name);
          }
        }

        // Insert new tags if any
        if (toCreate.length > 0) {
          const { data: newTags, error: insertError } = await supabase
            .from('tags')
            .insert(toCreate.map((name) => ({ name, user_id: session.user.id })))
            .select();

          if (insertError) throw insertError;

          if (newTags) {
            resolvedIds.push(...newTags.map((t) => t.id));
            // Update local tags state
            setTags((prev) => [...prev, ...newTags].sort((a, b) => a.name.localeCompare(b.name)));
          }
        }

        return { ids: resolvedIds, error: null };
      } catch (err: any) {
        return { ids: [], error: err.message };
      }
    },
    [session, tags]
  );

  // Create Item
  const createItem = useCallback(
    async (
      itemInput: {
        title: string;
        item_type: ItemType;
        payload: Record<string, any>;
        favorite?: boolean;
        storage_ref?: string | null;
        storage_meta?: any;
      },
      tagNames: string[] = []
    ) => {
      if (!session?.user?.id) return { data: null, error: 'Unauthenticated' };

      try {
        // 1. Ensure tags exist and get their IDs
        const { ids: tagIds, error: tagError } = await ensureTags(tagNames);
        if (tagError) throw new Error(tagError);

        // 2. Insert item
        const { data: newItemData, error: itemError } = await supabase
          .from('items')
          .insert({
            user_id: session.user.id,
            item_type: itemInput.item_type,
            title: itemInput.title,
            payload: itemInput.payload,
            favorite: itemInput.favorite || false,
            storage_ref: itemInput.storage_ref || null,
            storage_meta: itemInput.storage_meta || null,
          })
          .select()
          .single();

        if (itemError) throw itemError;

        const createdItem: Item = {
          ...newItemData,
          tags: [],
        };

        // 3. Link tags in item_tags join table
        if (tagIds.length > 0) {
          const { error: linkError } = await supabase
            .from('item_tags')
            .insert(tagIds.map((tagId) => ({ item_id: createdItem.id, tag_id: tagId })));

          if (linkError) throw linkError;

          // Attach tags to local object
          const attachedTags = tags.filter((t) => tagIds.includes(t.id));
          createdItem.tags = attachedTags;
        }

        // 4. Update local state
        setItems((prev) => [createdItem, ...prev]);

        return { data: createdItem, error: null };
      } catch (err: any) {
        console.error('[vault] Create item error:', err);
        return { data: null, error: err.message || 'Failed to create item' };
      }
    },
    [session, ensureTags, tags]
  );

  // Update Item
  const updateItem = useCallback(
    async (
      id: string,
      updates: {
        title?: string;
        payload?: Record<string, any>;
        favorite?: boolean;
        storage_ref?: string | null;
        storage_meta?: any;
      },
      tagNames?: string[]
    ) => {
      if (!session?.user?.id) return { data: null, error: 'Unauthenticated' };

      try {
        // 1. Update the item row
        const { data: updatedItemData, error: itemError } = await supabase
          .from('items')
          .update({
            ...updates,
            updated_at: new Date().toISOString(),
          })
          .eq('id', id)
          .select()
          .single();

        if (itemError) throw itemError;

        const updatedItem: Item = {
          ...updatedItemData,
          tags: [],
        };

        // 2. Handle tag associations if tags are provided
        if (tagNames !== undefined) {
          const { ids: tagIds, error: tagError } = await ensureTags(tagNames);
          if (tagError) throw new Error(tagError);

          // Delete old links
          const { error: unlinkError } = await supabase
            .from('item_tags')
            .delete()
            .eq('item_id', id);

          if (unlinkError) throw unlinkError;

          // Insert new links
          if (tagIds.length > 0) {
            const { error: linkError } = await supabase
              .from('item_tags')
              .insert(tagIds.map((tagId) => ({ item_id: id, tag_id: tagId })));

            if (linkError) throw linkError;
          }

          // Fetch or map the attached tags from our tags state
          const attachedTags = tags.filter((t) => tagIds.includes(t.id));
          updatedItem.tags = attachedTags;
        } else {
          // Preserve old tags if no tags update requested
          const oldItem = items.find((it) => it.id === id);
          updatedItem.tags = oldItem?.tags || [];
        }

        // 3. Update local state
        setItems((prev) =>
          prev.map((item) => (item.id === id ? updatedItem : item)).sort(
            (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
          )
        );

        return { data: updatedItem, error: null };
      } catch (err: any) {
        console.error('[vault] Update item error:', err);
        return { data: null, error: err.message || 'Failed to update item' };
      }
    },
    [session, ensureTags, tags, items]
  );

  // Delete Item
  const deleteItem = useCallback(
    async (id: string) => {
      try {
        // Optimistic update
        setItems((prev) => prev.filter((item) => item.id !== id));

        const { error: deleteError } = await supabase
          .from('items')
          .delete()
          .eq('id', id);

        if (deleteError) throw deleteError;

        return { error: null };
      } catch (err: any) {
        console.error('[vault] Delete item error:', err);
        // Rollback on error by reloading
        fetchData();
        return { error: err.message || 'Failed to delete item' };
      }
    },
    [fetchData]
  );

  // Toggle Favorite (Highly Optimistic)
  const toggleFavorite = useCallback(
    async (id: string) => {
      const item = items.find((it) => it.id === id);
      if (!item) return;

      const nextFavorite = !item.favorite;

      // Optimistic update
      setItems((prev) =>
        prev.map((it) => (it.id === id ? { ...it, favorite: nextFavorite } : it))
      );

      try {
        const { error: updateError } = await supabase
          .from('items')
          .update({ favorite: nextFavorite })
          .eq('id', id);

        if (updateError) throw updateError;
      } catch (err) {
        console.error('[vault] Toggle favorite error:', err);
        // Rollback on error
        setItems((prev) =>
          prev.map((it) => (it.id === id ? { ...it, favorite: item.favorite } : it))
        );
      }
    },
    [items]
  );

  // Create Tag directly
  const createTag = useCallback(
    async (name: string) => {
      if (!session?.user?.id) return { data: null, error: 'Unauthenticated' };
      const tagName = name.trim();
      if (!tagName) return { data: null, error: 'Tag name cannot be empty' };

      // Check if tag already exists in local state
      const existing = tags.find((t) => t.name.toLowerCase() === tagName.toLowerCase());
      if (existing) return { data: existing, error: null };

      try {
        const { data: newTag, error: insertError } = await supabase
          .from('tags')
          .insert({ name: tagName, user_id: session.user.id })
          .select()
          .single();

        if (insertError) throw insertError;

        setTags((prev) => [...prev, newTag].sort((a, b) => a.name.localeCompare(b.name)));
        return { data: newTag, error: null };
      } catch (err: any) {
        console.error('[vault] Create tag error:', err);
        return { data: null, error: err.message || 'Failed to create tag' };
      }
    },
    [session, tags]
  );

  // Delete Tag
  const deleteTag = useCallback(
    async (id: string) => {
      try {
        // Optimistic update
        setTags((prev) => prev.filter((t) => t.id !== id));
        // Remove tag from items in local state
        setItems((prevItems) =>
          prevItems.map((item) => ({
            ...item,
            tags: item.tags?.filter((t) => t.id !== id) || [],
          }))
        );

        const { error: deleteError } = await supabase
          .from('tags')
          .delete()
          .eq('id', id);

        if (deleteError) throw deleteError;

        return { error: null };
      } catch (err: any) {
        console.error('[vault] Delete tag error:', err);
        fetchData();
        return { error: err.message || 'Failed to delete tag' };
      }
    },
    [fetchData]
  );

  const value = useMemo(
    () => ({
      items,
      tags,
      loading,
      error,
      refresh: fetchData,
      createItem,
      updateItem,
      deleteItem,
      toggleFavorite,
      createTag,
      deleteTag,
    }),
    [items, tags, loading, error, fetchData, createItem, updateItem, deleteItem, toggleFavorite, createTag, deleteTag]
  );

  return <VaultContext.Provider value={value}>{children}</VaultContext.Provider>;
}

export function useVault() {
  const ctx = useContext(VaultContext);
  if (!ctx) {
    throw new Error('useVault must be used inside a <VaultProvider>');
  }
  return ctx;
}
